import {standardKeymap as cmStandardKeymap} from '@codemirror/commands'
import {EditorState, type Extension} from '@codemirror/state'
import type {KeyBinding} from '@codemirror/view'

/**
 * Hack: Forces the CodeMirror editor to function as a single-line editor by configuring the line separator to be nonsense,
 * causing everything in the entire editor to render under a single `cm-line` element. This allows us to control the
 * line breaks ourselves, allowing us to create multi-line marks because, as CodeMirror sees it, they aren't multiline
 * at all. This breaks a lot of CodeMirror's expectations about how the content will behave, but it's no big deal
 * because we aren't using a lot of the features that depend on this anyway.
 */
export const singleLineEditorExtension: Extension = [
  // We have to set the line separator to something, so I used a private unicode character that shouldn't appear in
  // user content since it has no visible representation
  // TODO: Ensure it doesn't appear in user content by filtering it out
  EditorState.lineSeparator.of(String.fromCodePoint(0xe000)),
]

// CodeMirror's standard keybindings do not work reliably with our single-line editor.
// To work around this, we rely on the browser's native keybindings.
// The list below identifies keybindings that fail and thus should be removed from the default keymap.
// Related issue: https://github.com/github/core-ux/issues/745
// https://github.com/codemirror/commands/blob/2ff2e3df028d264c4b4f0cab984fb5bb859a8c81/src/commands.ts#L961
const invalidEmacsStyleKeymap = [{key: 'Ctrl-a'}, {key: 'Ctrl-e'}, {key: 'Ctrl-k'}].map(b => ({
  mac: b.key,
}))

// https://github.com/codemirror/commands/blob/2ff2e3df028d264c4b4f0cab984fb5bb859a8c81/src/commands.ts#L1013
const invalidStandardKeymap = [
  {mac: 'Cmd-ArrowLeft'},
  {mac: 'Cmd-ArrowRight'},
  {mac: 'Ctrl-ArrowUp'},
  {mac: 'Ctrl-ArrowDown'},
  {key: 'Home'}, // TODO: test
  {key: 'Mod-Home'}, // TODO: test
  {key: 'End'}, // TODO: test
  {key: 'Mod-End'}, // TODO: test
  {mac: 'Mod-Backspace'},
  {mac: 'Mod-Delete'},
]

// Filter invalid keybindings from the CodeMirror's standard keymap
const removeInvalidStandardKeymap = (): KeyBinding[] => {
  const invalidKeys = [...invalidStandardKeymap, ...invalidEmacsStyleKeymap]
  const filtered = cmStandardKeymap.filter(
    kb =>
      !invalidKeys.find(
        inv => (inv.mac && kb.mac && inv.mac === kb.mac) || ('key' in inv && kb.key && inv.key === kb.key),
      ),
  )
  return filtered
}

const filteredStandardKeymap = removeInvalidStandardKeymap()
export const singleLineEditorDefaultKeymap: KeyBinding[] = [
  // Fix enter key handling since the editor will insert its line separator by default
  {
    key: 'Enter',
    run: view => {
      view.dispatch(view.state.update(view.state.replaceSelection('\n'), {scrollIntoView: true, userEvent: 'input'}))
      return true
    },
  },
  ...filteredStandardKeymap,
]
